import H from '@here/maps-api-for-javascript';
import { OnyxScheduleCompliance } from '../../../enums';
import { CssVarHelper } from '../../../helpers/css-var.helper';
import { OnyxMapRouteVariant, OnyxRouteLegType } from '../enums';
import type { OnyxMapRoutePoint } from '../interfaces';

const EMPTY_DASH = 1 as const;
const EMPTY_GAP_DASH = 1 as const;
const OFF_ROUTE_DASH = 2 as const;
const OFF_ROUTE_GAP_DASH = 1 as const;

export interface PolylineOptions extends Pick<
  OnyxMapRoutePoint,
  'active' | 'compliance'
> {
  style: OnyxRouteLegType | 'empty' | 'off-route';
  completed: boolean;
  variant?: OnyxMapRouteVariant;
}
type PolylineStyles = H.map.SpatialStyle.Options[];

export const getPolylineStyles = (
  options: PolylineOptions,
  document: Document,
): PolylineStyles => {
  const token = (name: string): string =>
    CssVarHelper.read(`--onyx-color-route-${name}`, document);

  const {
    style,
    variant = OnyxMapRouteVariant.PRIMARY,
    active,
    compliance,
    completed,
  } = options;

  let buildPolylineStyles;
  switch (style) {
    case 'empty':
      buildPolylineStyles = buildEmptyPolylineStyles;
      break;

    case 'off-route':
      buildPolylineStyles = buildOffRoutePolylineStyles;
      break;

    case OnyxRouteLegType.VEHICLE:
    case OnyxRouteLegType.TUNNEL:
      buildPolylineStyles = buildVehiclePolylineStyles;
      break;

    case OnyxRouteLegType.FERRY:
      buildPolylineStyles = buildFerryPolylineStyles;
      break;

    case OnyxRouteLegType.TRAIN:
      buildPolylineStyles = buildTrainPolylineStyles;
      break;

    default:
      throw new Error(`Unknown polyline style: ${style}`);
  }

  const isSecondary = variant === OnyxMapRouteVariant.SECONDARY;
  const activeBorder = isSecondary
    ? token('second-outline')
    : token('first-outline');
  const activeBackground = isSecondary ? token('second') : token('first');
  const inactiveBorder = isSecondary
    ? token('second-outline')
    : token('first-outline');
  const inactiveBackground = isSecondary
    ? token('second-inactive')
    : token('first-inactive');

  if (style === 'off-route') {
    return buildOffRoutePolylineStyles(
      active ? activeBorder : inactiveBorder,
      active ? activeBackground : inactiveBackground,
    );
  } else if (variant === OnyxMapRouteVariant.ALTERNATIVE) {
    return buildPolylineStyles(token('neutral-outline'), token('alternative'));
  } else if (!active) {
    return buildPolylineStyles(
      completed ? token('neutral-outline') : inactiveBorder,
      completed ? token('completed') : inactiveBackground,
    );
  }

  if (completed && compliance === OnyxScheduleCompliance.ON_TIME) {
    return buildPolylineStyles(token('on-time-outline'), token('on-time'));
  }

  if (!completed || !compliance) {
    return buildPolylineStyles(activeBorder, activeBackground);
  }

  switch (compliance) {
    case OnyxScheduleCompliance.DELAYED:
      return buildPolylineStyles(token('delayed-outline'), token('delayed'));
    case OnyxScheduleCompliance.DELAY_RISK:
      return buildPolylineStyles(
        token('delay-risk-outline'),
        token('delay-risk'),
      );
    case OnyxScheduleCompliance.ON_TIME:
      return buildPolylineStyles(activeBorder, activeBackground);
  }
};

const buildEmptyPolylineStyles = (
  borderColor: string,
  backgroundColor: string,
): PolylineStyles => [
  {
    strokeColor: borderColor,
    fillColor: 'transparent',
    lineWidth: 8,
    lineCap: 'butt',
    lineDash: [EMPTY_DASH, EMPTY_GAP_DASH],
    lineDashImage: H.map.SpatialStyle.DashImage.CIRCLE,
  },
  {
    strokeColor: backgroundColor,
    fillColor: 'transparent',
    lineWidth: 6,
    lineCap: 'butt',
    lineDash: [EMPTY_DASH * (8 / 6), EMPTY_GAP_DASH * (8 / 6)],
    lineDashImage: H.map.SpatialStyle.DashImage.CIRCLE,
  },
];

const buildOffRoutePolylineStyles = (
  borderColor: string,
  backgroundColor: string,
): PolylineStyles => [
  {
    strokeColor: borderColor,
    fillColor: 'transparent',
    lineWidth: 8,
    lineCap: 'butt',
    lineDash: [OFF_ROUTE_DASH, OFF_ROUTE_GAP_DASH],
  },
  {
    strokeColor: backgroundColor,
    fillColor: 'transparent',
    lineWidth: 6,
    lineCap: 'butt',
    lineDash: [OFF_ROUTE_DASH * (8 / 6), OFF_ROUTE_GAP_DASH * (8 / 6)],
  },
];

const buildVehiclePolylineStyles = (
  borderColor: string,
  backgroundColor: string,
): PolylineStyles => [
  {
    strokeColor: borderColor,
    fillColor: 'transparent',
    lineWidth: 8,
  },
  {
    strokeColor: backgroundColor,
    fillColor: 'transparent',
    lineWidth: 6,
  },
];

const buildFerryPolylineStyles = (
  borderColor: string,
  backgroundColor: string,
): PolylineStyles => {
  return [
    {
      strokeColor: borderColor,
      fillColor: 'transparent',
      lineWidth: 8,
    },
    {
      strokeColor: backgroundColor,
      fillColor: 'transparent',
      lineWidth: 5,
      lineCap: 'butt',
      lineDash: [EMPTY_DASH, EMPTY_GAP_DASH],
      lineDashImage: H.map.SpatialStyle.DashImage.CIRCLE,
    },
  ];
};

const buildTrainPolylineStyles = (
  borderColor: string,
  backgroundColor: string,
): PolylineStyles => {
  return [
    {
      strokeColor: borderColor,
      fillColor: 'transparent',
      lineWidth: 8,
    },
    {
      strokeColor: backgroundColor,
      fillColor: 'transparent',
      lineWidth: 4,
      lineDash: [6, 4],
    },
  ];
};
